import React, { useState } from "react";
import "./Dashboard.css";
import "../../cssfiles/homepage.css";
import { ThemeContext } from "../Homepage";
import { useContext } from "react";
import { format } from "date-fns";
import {
  MenuItem,
  Select,
  FormControl,
  Button,
  Popover,
  TextField,
} from "@mui/material";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRange } from "react-date-range";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

const timePeriodOptions = [
  "Today",
  "Yesterday",
  "Last 24 Hours",
  "This Week",
  "Last Week",
  "Last 7 Days",
  "This Month",
  "Last Month",
  "Last 30 Days",
  "This Year",
  "Last Year",
  "Custom",
];

function CustomTimePeriod({
  setDateTime,
  width,
  setSelectedOption,
  defaultValue,
}) {
  const { theme } = useContext(ThemeContext);

  const [selectedValue, setSelectedValue] = useState(
    defaultValue ? defaultValue : "",
  );
  const spanRef = React.useRef();
  const [customDateRange, setCustomDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isDateRangeConfirmed, setIsDateRangeConfirmed] = useState(false);

  // Helper to get "HH:MM" from a Date object
  const toTimeString = (date) =>
    `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

  const now = new Date();

  // Time state for custom range (HH:MM string) — default to current time
  const [startTime, setStartTime] = useState(toTimeString(now));
  const [endTime, setEndTime] = useState(toTimeString(now));

  // Max allowed time — always current timestamp (re-evaluated on render for live cap)
  const maxDate = new Date();
  const maxTimeString = toTimeString(maxDate);

  const handleSelectChange = (event) => {
    const value = event.target.value;
    if (timePeriodOptions[timePeriodOptions.length - 1] !== "Custom") {
      timePeriodOptions.pop();
    }
    setSelectedValue(value);
    setIsSelectOpen(false);

    var startdate;
    var enddate;
    const now = new Date();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const last24Hours = new Date(now);
    last24Hours.setHours(now.getHours() - 24);

    const currDay = now.getDay();
    const lastSunday = new Date(now);
    lastSunday.setDate(now.getDate() - currDay - 7);
    const lastSaturday = new Date(lastSunday);
    lastSaturday.setDate(lastSunday.getDate() + 6);

    const thisSunday = new Date(now);
    thisSunday.setDate(now.getDate() - currDay);

    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);

    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const lastYear = now.getFullYear() - 1;

    if (value !== "Custom") {
      switch (event.target.value) {
        case "Today":
          startdate = new Date(now.setHours(0, 0, 0, 0)).toString();
          enddate = new Date().toString();
          break;
        case "Yesterday":
          startdate = new Date(
            yesterday.getFullYear(),
            yesterday.getMonth(),
            yesterday.getDate(),
            0,
            0,
            0,
            0,
          ).toString();
          enddate = new Date(
            yesterday.getFullYear(),
            yesterday.getMonth(),
            yesterday.getDate(),
            23,
            59,
            59,
            999,
          ).toString();
          break;
        case "Last 24 Hours":
          startdate = last24Hours.toString();
          enddate = now.toString();
          break;
        case "This Week":
          startdate = new Date(
            thisSunday.getFullYear(),
            thisSunday.getMonth(),
            thisSunday.getDate(),
            0,
            0,
            0,
            0,
          ).toString();
          enddate = now.toString();
          break;
        case "Last Week":
          startdate = new Date(
            lastSunday.getFullYear(),
            lastSunday.getMonth(),
            lastSunday.getDate(),
            0,
            0,
            0,
            0,
          ).toString();
          enddate = new Date(
            lastSaturday.getFullYear(),
            lastSaturday.getMonth(),
            lastSaturday.getDate(),
            23,
            59,
            59,
            999,
          ).toString();
          break;
        case "Last 7 Days":
          startdate = new Date(
            sevenDaysAgo.getFullYear(),
            sevenDaysAgo.getMonth(),
            sevenDaysAgo.getDate(),
            0,
            0,
            0,
            0,
          ).toString();
          enddate = now.toString();
          break;
        case "This Month":
          startdate = new Date(
            now.getFullYear(),
            now.getMonth(),
            1,
            0,
            0,
            0,
            0,
          ).toString();
          enddate = now.toString();
          break;
        case "Last Month":
          startdate = new Date(
            now.getFullYear(),
            now.getMonth() - 1,
            1,
            0,
            0,
            0,
            0,
          ).toString();
          enddate = new Date(
            now.getFullYear(),
            now.getMonth(),
            0,
            23,
            59,
            59,
            999,
          ).toString();
          break;
        case "Last 30 Days":
          startdate = new Date(
            thirtyDaysAgo.getFullYear(),
            thirtyDaysAgo.getMonth(),
            thirtyDaysAgo.getDate(),
            0,
            0,
            0,
            0,
          ).toString();
          enddate = now.toString();
          break;
        case "This Year":
          startdate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0).toString();
          enddate = now.toString();
          break;
        case "Last Year":
          startdate = new Date(lastYear, 0, 1, 0, 0, 0, 0).toString();
          enddate = new Date(lastYear, 11, 31, 23, 59, 59, 999).toString();
          break;
      }
      setDateTime(startdate, enddate);
      setSelectedOption(value);
    }
  };

  const handleMenuItemClick = (event, period) => {
    if (period === "Custom") {
      setAnchorEl(spanRef.current);
      // Reset date range and times to current timestamp
      const currentNow = new Date();
      setCustomDateRange([
        { startDate: currentNow, endDate: currentNow, key: "selection" },
      ]);
      setStartTime(toTimeString(currentNow));
      setEndTime(toTimeString(currentNow));
      setIsDateRangeConfirmed(false);
    } else {
      setAnchorEl(null);
    }
    setSelectedValue(period);
  };

  const handleDateChange = (item) => {
    setCustomDateRange([item.selection]);
    const { startDate, endDate } = item.selection;
    if (startDate && endDate && startDate !== endDate) {
      setIsDateRangeConfirmed(true);
    } else {
      setIsDateRangeConfirmed(false);
    }
  };

  // Helper to parse "HH:MM" into { hours, minutes }
  const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return {
      hours: isNaN(hours) ? 0 : hours,
      minutes: isNaN(minutes) ? 0 : minutes,
    };
  };

  const handleCustomDateConfirm = () => {
    const { hours: startHours, minutes: startMinutes } = parseTime(startTime);
    const { hours: endHours, minutes: endMinutes } = parseTime(endTime);

    const sdateupdate = new Date(customDateRange[0].startDate);
    sdateupdate.setHours(startHours, startMinutes, 0, 0);

    const edateupdate = new Date(customDateRange[0].endDate);
    edateupdate.setHours(endHours, endMinutes, 59, 999);

    const start = format(sdateupdate, "MM/dd/yyyy HH:mm");
    const end = format(edateupdate, "MM/dd/yyyy HH:mm");
    const customLabel = `${start} - ${end}`;

    if (timePeriodOptions[timePeriodOptions.length - 1] !== "Custom") {
      timePeriodOptions.pop();
    }
    timePeriodOptions.push(customLabel);

    setDateTime(sdateupdate, edateupdate);
    setSelectedOption("Custom");
    setSelectedValue(customLabel);
    setAnchorEl(null);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleSelectOpen = () => {
    if (timePeriodOptions[timePeriodOptions.length - 1] !== "Custom") {
      timePeriodOptions.pop();
    }
    setIsSelectOpen(true);
  };

  const inputSx = {
    "& .MuiInputBase-input": {
      fontSize: "12px",
      fontFamily: "Roboto",
      padding: "4px 6px",
      color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme === "dark" ? "#666" : "#aaa",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#008CD7",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#008CD7",
    },
  };

  const labelSx = {
    fontSize: "11px",
    fontFamily: "Roboto",
    color: theme === "dark" ? "#aaa" : "#666",
    marginBottom: "4px",
  };

  return (
    <div>
      <FormControl>
        <Select
          variant="standard"
          name="select-time-period"
          value={selectedValue}
          onChange={handleSelectChange}
          onOpen={handleSelectOpen}
          open={isSelectOpen}
          displayEmpty
          defaultValue={defaultValue ? defaultValue : "Custom"}
          style={{ paddingTop: "0px", paddingBottom: "0px" }}
          IconComponent={() => (
            <ExpandMoreOutlinedIcon style={{ width: "16px", height: "16px" }} />
          )}
          sx={{
            border: 0,
            width: { width: width ? `${width}px` : "617px" },
            bgcolor: "transparent",
            color: "var(--GreyVariation_2)",
            fontSize: "16px",
            fontFamily: "Roboto",
            fontWeight: 400,
            textAlign: "left",
            borderRadius: 0,
            marginRight: "0px",
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              { border: 0 },
            "& .MuiOutlinedInput-notchedOutline": {
              width: "122.35px",
              border: 0,
              padding: 0,
            },
            "& .MuiInputLabel-root": { display: "none", legend: "none" },
            "& .MuiSelect-select": { paddingTop: "0px", paddingBottom: "0px" },
          }}
          InputLabelProps={{ shrink: false }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: theme === "dark" ? "#3C3C3C" : "#C1C1C1",
                borderBottom: "2px solid #008CD7",
                borderRadius: 0,
                boxShadow: "none",
                maxHeight: "550px",
                overflow: "auto",
                color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
              },
            },
          }}
          ref={spanRef}
        >
          {timePeriodOptions.map((period) => (
            <MenuItem
              sx={{
                fontSize: "12px",
                fontFamily: "Roboto",
                fontWeight: 400,
                "& .MuiMenuItem.Mui-selected": "pink",
              }}
              key={period}
              value={period}
              onClick={(event) => handleMenuItemClick(event, period)}
            >
              {period}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: theme === "dark" ? "#2e2e2e" : "#ffffff",
            color: theme === "dark" ? "#fff" : "#3C3C3C",
          },
        }}
      >
        <div style={{ padding: "20px" }}>
          {/* Date Range Picker */}
          <DateRange
            editableDateInputs={true}
            onChange={handleDateChange}
            moveRangeOnFirstSelection={false}
            ranges={customDateRange}
            maxDate={maxDate}
          />

          {/* Time Selection Row */}
          {(() => {
            const todayStr = format(maxDate, "yyyy-MM-dd");
            const startDateStr = format(
              customDateRange[0].startDate,
              "yyyy-MM-dd",
            );
            const endDateStr = format(customDateRange[0].endDate, "yyyy-MM-dd");
            const startIsToday = startDateStr === todayStr;
            const endIsToday = endDateStr === todayStr;
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "16px",
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: `1px solid ${theme === "dark" ? "#444" : "#ddd"}`,
                }}
              >
                {/* Start Time */}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={labelSx}>Start Time</span>
                  <TextField
                    type="time"
                    value={startTime}
                    onChange={(e) => {
                      const val =
                        startIsToday && e.target.value > maxTimeString
                          ? maxTimeString
                          : e.target.value;
                      setStartTime(val);
                    }}
                    size="small"
                    inputProps={{
                      step: 60,
                      max: startIsToday ? maxTimeString : undefined,
                    }}
                    sx={inputSx}
                  />
                </div>

                {/* Separator */}
                <span
                  style={{
                    fontSize: "14px",
                    color: theme === "dark" ? "#aaa" : "#666",
                    paddingBottom: "6px",
                  }}
                >
                  –
                </span>

                {/* End Time */}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={labelSx}>End Time</span>
                  <TextField
                    type="time"
                    value={endTime}
                    onChange={(e) => {
                      const val =
                        endIsToday && e.target.value > maxTimeString
                          ? maxTimeString
                          : e.target.value;
                      setEndTime(val);
                    }}
                    size="small"
                    inputProps={{
                      step: 60,
                      max: endIsToday ? maxTimeString : undefined,
                    }}
                    sx={inputSx}
                  />
                </div>

                {/* Confirm Button pushed to the right */}
                <div style={{ marginLeft: "auto" }}>
                  <Button
                    onClick={handleCustomDateConfirm}
                    variant="contained"
                    disabled={!isDateRangeConfirmed}
                    sx={{
                      backgroundColor: "#008CD7",
                      fontSize: "12px",
                      fontFamily: "Roboto",
                      textTransform: "none",
                      padding: "5px 16px",
                      "&:hover": { backgroundColor: "#006fa8" },
                      "&.Mui-disabled": {
                        backgroundColor: theme === "dark" ? "#444" : "#ccc",
                        color: theme === "dark" ? "#666" : "#999",
                      },
                    }}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      </Popover>
    </div>
  );
}

export default CustomTimePeriod;
